import { useAuthStore } from "@/stores/authStore";
import type { WorkshopAttribution } from "@/types/workshop.type";

/** The signed-in user's id, so their own work reads as "you". */
export function useViewerId(): string | null {
  return useAuthStore((state) => state.user?.id ?? null);
}

function person(a: WorkshopAttribution, viewerId: string | null): string {
  if (a.userId && a.userId === viewerId) return "you";
  return a.userName ?? "a team member";
}

/** Who wrote it: "you", "the assistant for Rana", "agent “TikTok audit”". */
function actor(a: WorkshopAttribution, viewerId: string | null): string {
  if (a.actor === "agent") {
    return a.deployment?.name ? `agent “${a.deployment.name}”` : "a removed agent";
  }
  if (a.actor === "assistant") return `the assistant for ${person(a, viewerId)}`;
  return person(a, viewerId);
}

/**
 * Where, when it adds anything. Threads are private, so another member's is
 * never named — the server withholds its title anyway.
 */
function context(a: WorkshopAttribution, viewerId: string | null): string | null {
  if (a.actor === "agent") return `deployed by ${person(a, viewerId)}`;
  if (a.actor !== "assistant" || !a.thread) return null;
  if (a.thread.state === "own") return a.thread.title ? `“${a.thread.title}”` : "your thread";
  if (a.thread.state === "deleted") return "a deleted thread";
  return "their chat";
}

/** "by the assistant for you · “Q3 sweep”" — prefix it with the verb that fits. */
export function formatAttribution(
  a: WorkshopAttribution | null | undefined,
  viewerId: string | null,
  options?: { includeContext?: boolean },
): string | null {
  if (!a) return null;
  const where = options?.includeContext === false ? null : context(a, viewerId);
  return `by ${actor(a, viewerId)}${where ? ` · ${where}` : ""}`;
}

/** A hover title for a compact row: "Created by … on Sep 15, 2026, 14:05". */
export function attributionTitle(
  verb: string,
  a: WorkshopAttribution | null | undefined,
  viewerId: string | null,
): string | undefined {
  const by = formatAttribution(a, viewerId);
  if (!a || !by) return undefined;
  const when = new Date(a.at).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
  return `${verb} ${by}, ${when}`;
}
